import React, { FC, MutableRefObject, ReactElement, useContext, useEffect, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { CRMServerResponse, SelectedCRMConfigEntry } from '../Types/Types'
import { CRM } from '../Main/CRM'
import MessageContext, {
    MessageContextProvider,
    MessageContextType
} from '../../../../../../React/Common/Contexts/MessageContext'
import { ReactState } from '../../../../../../React/Common/Types/Types'
import { IsVarEmpty, isVarNotEmpty } from '../../../../../../NonReact/utils/GlobalFunctions'
import { ModalLoading } from '../../../../../../React/Common/Components/ModalLoading'
import CLanguageHandler from '../../../../../../NonReact/Languages/LanguageHandler'
import { CRMModal as CRMModalLanguage, GenericModal } from '../../../../../../../i18n'
import { CommonFormContextProvider } from '../../../../../../React/Common/Contexts/CommonFormContext'
import { Modal } from '../../../../Molecules/Modal/Modal'
import {
    LanguageContextProvider,
    PromptContextProvider,
    UserInfoContextProvider
} from '../../../../../../React/Common/Contexts'
import { $window } from '../../../../../../NonReact/common/AkixiWindow'
import { getCRMConfiguration } from '../../../../../../NonReact/common/API/EntityAPIClient'
import { APIData, JSONResponse } from '../../../../../../NonReact/common/Types/Types'
import { AdminFilterSingleValues } from '../../../../Molecules/AdminFilters/AdminFilters'

type Props = {
    closeModal: (refresh: boolean) => void
    filters: Nullable<AdminFilterSingleValues>
    openState: ReactState<boolean>
    selectedCRMConfig?: SelectedCRMConfigEntry
    isViewOnly: boolean
}

type ModifyAddDirectoryContainerProps = {
    closeModal: (refresh: boolean) => void
    filters: Nullable<AdminFilterSingleValues>
    selectedCRMConfig?: SelectedCRMConfigEntry
    isViewOnly: boolean
}

const CRMTabsContainer: FC<ModifyAddDirectoryContainerProps> = (
    props: ModifyAddDirectoryContainerProps
): ReactElement => {
    const { displayError }: MessageContextType = useContext(MessageContext)

    const [currentCRMConfiguration, setCurrentCRMConfiguration]: ReactState<Nullable<CRMServerResponse>> =
        useState<Nullable<CRMServerResponse>>(null)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const { selectedCRMConfig, closeModal }: ModifyAddDirectoryContainerProps = props

    useEffect((): void => {
        if (!IsVarEmpty(selectedCRMConfig)) {
            getCRMConfiguration(
                selectedCRMConfig.telsysID,
                selectedCRMConfig.partitionID,
                selectedCRMConfig.deviceID,
                selectedCRMConfig.id
            )
                .then((response: JSONResponse<APIData<CRMServerResponse>>): void => {
                    if (isMounted.current) setCurrentCRMConfiguration(response?.Data)
                })
                .catch((error: Error): void => {
                    displayError(error)
                    closeModal(true)
                })
        }
    }, [])

    if (!IsVarEmpty(selectedCRMConfig) && currentCRMConfiguration === null) {
        return <ModalLoading tabs={1} sections={4} />
    }

    return <CRM {...props} currentCRMConfiguration={currentCRMConfiguration} />
}

const CRMModal: FC<Props> = ({
    filters,
    openState: [isOpen],
    selectedCRMConfig,
    isViewOnly,
    closeModal: modalClose
}: Props): ReactElement => {
    // Access language resources directly without context as this is top level component.
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(CRMModalLanguage.NAME)
    language.loadBundle(GenericModal.NAME)

    const closeModal = (): void => {
        $window.disableHotKeys = false
        modalClose(false)
    }

    return (
        <div>
            <CommonFormContextProvider>
                <Modal
                    inlineExitPromptSettings={{
                        message: language.get(GenericModal.GenericModal_PromptMessage),
                        confirmButtonText: language.get(GenericModal.GenericModal_PromptConfirm),
                        cancelButtonText: language.get(GenericModal.GenericModal_PromptCancel),
                        isRouterMode: true
                    }}
                    className='crm-modal'
                    isOpen={isOpen}
                    onClose={closeModal}
                >
                    {isOpen ? (
                        <LanguageContextProvider>
                            <PromptContextProvider>
                                <MessageContextProvider>
                                    <UserInfoContextProvider>
                                        <div className='modal-title-report-config'>
                                            {isVarNotEmpty(selectedCRMConfig)
                                                ? language.get(CRMModalLanguage.ModifyCRMTitle)
                                                : language.get(CRMModalLanguage.AddCRMTitle)}
                                        </div>
                                        <CRMTabsContainer
                                            filters={filters}
                                            selectedCRMConfig={selectedCRMConfig}
                                            closeModal={modalClose}
                                            isViewOnly={isViewOnly}
                                        />
                                    </UserInfoContextProvider>
                                </MessageContextProvider>
                            </PromptContextProvider>
                        </LanguageContextProvider>
                    ) : (
                        <> </>
                    )}
                </Modal>
            </CommonFormContextProvider>
        </div>
    )
}

export default CRMModal
